/**
 * Error Cides
 *
 * Why: Consistent,machine-readable error identification
 * what: Unique codes for each error type
 *
 * Benefits:
 *      - Easy to search logs for specific errors
 *      - can be used for ii8n message lookup
 *      - Machine-readable for automated handling
 *
 * Naming Conventions:
 *      - ERR_ prefic for all error codes
 *      - Category prefix (Network,Auth)
 *      - Descriptive suffix
 */

export const ERROR_CODES = {
  // ============================================
  // Network Errors (no response from server)
  // ============================================

  /** Generic network error */
  NETWORK_ERROR: "ERR_NETWORK_ERROR",
  NETWORK_TIMEOUT: "ERR_NETWORK_TIMEOUT",
  NETWORK_CANCELLED: "ERR_NETWORK_CANCELLED",
  NETWORK_DNS_FAILED: "ERR_NETWORK_DNS_FAILED",
  NETWORK_SSL_ERROR: "ERR_NETWORK_SSL_ERROR",
  NETWORK_CONNECTION_REFUSED: "ERR_NETWORK_CONNECTION_REFUSED",

  // ============================================
  // Authentication Errors (401)
  // ============================================
  AUTH_UNAUTHORIZED: "ERR_AUTH_UNAUTHORIZED",

  // we add more for session validation

  // ============================================
  // Authorization Errors (403)
  // ============================================

  /** Generic authorization error */
  AUTHZ_FORBIDDEN: "ERR_AUTHZ_FORBIDDEN",
  /** Insufficient permissions */
  AUTHZ_INSUFFICIENT_PERMISSIONS: "ERR_AUTHZ_INSUFFICIENT_PERMISSIONS",
  /** Resource access denied */
  AUTHZ_ACCESS_DENIED: "ERR_AUTHZ_ACCESS_DENIED",
  /** IP blocked */
  AUTHZ_IP_BLOCKED: "ERR_AUTHZ_IP_BLOCKED",

  // ============================================
  // Validation Errors (422)
  // ============================================

  /** Generic validation error */
  VALIDATION_ERROR: "ERR_VALIDATION_ERROR",
  /** Required field missing */
  VALIDATION_REQUIRED: "ERR_VALIDATION_REQUIRED",
  /** Invalid email format */
  VALIDATION_INVALID_EMAIL: "ERR_VALIDATION_INVALID_EMAIL",
  /** Invalid phone format */
  VALIDATION_INVALID_PHONE: "ERR_VALIDATION_INVALID_PHONE",
  /** Value too short */
  VALIDATION_TOO_SHORT: "ERR_VALIDATION_TOO_SHORT",
  /** Value too long */
  VALIDATION_TOO_LONG: "ERR_VALIDATION_TOO_LONG",
  /** Value out of range */
  VALIDATION_OUT_OF_RANGE: "ERR_VALIDATION_OUT_OF_RANGE",
  /** Invalid format */
  VALIDATION_INVALID_FORMAT: "ERR_VALIDATION_INVALID_FORMAT",

  // ============================================
  // Client Errors (4xx)
  // ============================================

  /** Bad request (400) */
  CLIENT_BAD_REQUEST: "ERR_CLIENT_BAD_REQUEST",
  /** Not found (404) */
  CLIENT_NOT_FOUND: "ERR_CLIENT_NOT_FOUND",
  /** Method not allowed (405) */
  CLIENT_METHOD_NOT_ALLOWED: "ERR_CLIENT_METHOD_NOT_ALLOWED",
  /** Conflict (409) */
  CLIENT_CONFLICT: "ERR_CLIENT_CONFLICT",
  /** Gone (410) */
  CLIENT_GONE: "ERR_CLIENT_GONE",
  /** Payload too large (413) */
  CLIENT_PAYLOAD_TOO_LARGE: "ERR_CLIENT_PAYLOAD_TOO_LARGE",
  /** Unsupported media type (415) */
  CLIENT_UNSUPPORTED_MEDIA: "ERR_CLIENT_UNSUPPORTED_MEDIA",

  // ============================================
  // Rate Limiting (429)
  // ============================================

  /** Too many requests */
  RATE_LIMIT_EXCEEDED: "ERR_RATE_LIMIT_EXCEEDED",
  /** Daily limit exceeded */
  RATE_LIMIT_DAILY: "ERR_RATE_LIMIT_DAILY",
  // ============================================
  // Server Errors (5xx)
  // ============================================

  /** Internal server error (500) */
  SERVER_INTERNAL_ERROR: "ERR_SERVER_INTERNAL_ERROR",
  /** Bad gateway (502) */
  SERVER_BAD_GATEWAY: "ERR_SERVER_BAD_GATEWAY",
  /** Service unavailable (503) */
  SERVER_UNAVAILABLE: "ERR_SERVER_UNAVAILABLE",
  /** Gateway timeout (504) */
  SERVER_GATEWAY_TIMEOUT: "ERR_SERVER_GATEWAY_TIMEOUT",

  // ============================================
  // Unknown/Generic
  // ============================================

  /** Unknown error */
  UNKNOWN_ERROR: "ERR_UNKNOWN_ERROR",
} as const;

/**
 * Type for error codes
 */
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];
